import { useState } from "react";
import { DateField } from "@/components/ui/date-field";
import WizardShell from "@/components/WizardShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtEur, fmtDate, loanMonthly } from "@/lib/format";

export default function TopupWizard({ open, onOpenChange, contract, onSaved }) {
  const [step, setStep] = useState(0);
  const [amount, setAmount] = useState("");
  const [dateVal, setDateVal] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setStep(0); setAmount(""); setResult(null); setDateVal(new Date().toISOString().slice(0, 10)); };

  const finish = async () => {
    if (result) { onOpenChange(false); reset(); return; }
    setLoading(true);
    try {
      const res = await api.post(`/contracts/${contract.id}/topup`, { amount: Number(amount) || 0, date: dateVal });
      setResult(res.data.amendment);
      setStep(1);
      toast.success("Lening opgehoogd — wijzigingsovereenkomst aangemaakt");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ophogen mislukt"); }
    finally { setLoading(false); }
  };

  return (
    <WizardShell open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}
      title="Lening ophogen"
      subtitle="Vanaf een datum — looptijd verlengt automatisch"
      steps={["Bedrag & datum", "Resultaat"]}
      step={step} setStep={setStep} onFinish={finish} loading={loading}
      canNext={step !== 0 || (Number(amount) > 0 && dateVal)}
      finishLabel={result ? "Sluiten" : "Ophogen & overeenkomst opstellen"}>
      {step === 0 && (
        <div className="space-y-4">
          <div className="bg-slate-50 border rounded-lg p-3 text-sm text-slate-600">
            Huidig maandbedrag blijft <b>{fmtEur(contract?.monthly_rent)}</b>. Door de verhoging wordt de resterende looptijd automatisch verlengd vanaf de gekozen datum.
          </div>
          <div><Label>Extra leenbedrag (€)</Label><Input type="number" data-testid="topup-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="5000" /></div>
          <div><Label>Ingangsdatum verhoging</Label><DateField testId="topup-date-input" value={dateVal} onChange={setDateVal} /></div>
        </div>
      )}
      {step === 1 && result && (
        <div className="space-y-3 text-sm">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 grid grid-cols-2 gap-3">
            <div><div className="text-xs text-slate-500">Saldo vóór verhoging</div><div className="font-bold">{fmtEur(result.balance_before)}</div></div>
            <div><div className="text-xs text-slate-500">Verhoging</div><div className="font-bold">{fmtEur(result.amount)}</div></div>
            <div><div className="text-xs text-slate-500">Nieuwe hoofdsom</div><div className="font-bold">{fmtEur(result.new_principal)}</div></div>
            <div><div className="text-xs text-slate-500">Maandbedrag (ongewijzigd)</div><div className="font-bold">{fmtEur(result.monthly)}</div></div>
            <div><div className="text-xs text-slate-500">Nieuwe looptijd</div><div className="font-bold">{result.remaining_months} maanden</div></div>
            <div><div className="text-xs text-slate-500">Nieuwe einddatum</div><div className="font-bold">{fmtDate(result.new_end_date)}</div></div>
          </div>
          <p className="text-xs text-slate-500">De wijzigingsovereenkomst is automatisch opgesteld en toegevoegd aan de tijdslijn (tab Brieven).</p>
        </div>
      )}
    </WizardShell>
  );
}
